import { Info } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { updatePortfolioValidation } from 'lib/api/integrations/digitalSignature';
import { getPortoflioValidations } from 'lib/api/integrations/digitalSignature';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { Portfolio, PortfolioValidation } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const UpdateValidations = ({
  open,
  onClose,
  portfolio,
  onPortfolioUpdated,
}: {
  open: boolean;
  onClose: () => void;
  portfolio: Portfolio;
  onPortfolioUpdated?: () => void;
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [validations, setValidations] = useState<PortfolioValidation[]>([]);
  const [selectedValidationIds, setSelectedValidationIds] = useState<number[]>(
    portfolio.portfolioValidations?.map((v) => v.id) ?? [],
  );

  const fetchValidations = async () => {
    const validations = await getPortoflioValidations(dispatch);
    setValidations(validations);
  };

  const handleValidationsChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setSelectedValidationIds(value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const selectedTypes = selectedValidationIds
        .map((id) => validations.find((v) => v.id === id)?.type)
        .filter((t): t is string => Boolean(t));
      await updatePortfolioValidation(dispatch, portfolio.id, {
        validations: selectedTypes,
      });
      onPortfolioUpdated?.();
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchValidations();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ px: 2.5, pt: 2 }}>
        <Title
          title="Actualizar validaciones"
          icon={<Info color="primary" />}
          color="primary"
        />
      </Box>
      <Box sx={{ px: 2.5, pb: 2 }}>
        <FormControl fullWidth margin="dense">
          <InputLabel id="portfolio-validations-label">
            Validaciones del portafolio
          </InputLabel>
          <Select
            labelId="portfolio-validations-label"
            id="portfolio-validations-select"
            multiple
            value={selectedValidationIds}
            label="Validaciones del portafolio"
            onChange={handleValidationsChange}
            renderValue={(selected) =>
              selected
                .map((t) => validations.find((v) => v.id === t))
                .filter((v): v is PortfolioValidation => Boolean(v))
                .map((v) => v.description)
                .join(', ')
            }
          >
            {validations.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                <Checkbox checked={selectedValidationIds.indexOf(v.id) > -1} />
                <ListItemText primary={v.description ?? ''} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <AlertBanner
          severity="warning"
          title="Advertencia"
          message="Actualizar los grupos de validación determinará si este portafolio es incluido o excluido cuando se realicen las validaciones para cada grupo."
        />
      </Box>

      <DialogActions>
        <CustomButton color="primary" onClick={handleSave}>
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateValidations;
